import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { rem } from 'polished'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import Clickable from 'components/Common/Clickable'
import PaletteSheet from 'LuxKit/PaletteSheet'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import useCountdownTagOptions from 'home/hooks/useCountdownTagOptions'
import FlightDealPoints from './FlightDealPoints'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'

const FlightCardContainer = styled(Clickable)`
  display: grid;
  grid-template-rows: ${rem(155)} 1fr;
  position: relative;
  width: ${rem(300)};
  overflow: hidden;
  transition: background-color 0.2s;

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }

  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: var(--border-radius-m);
  }
`

const DealContainer = styled(Group)`
  padding: ${rem(8)};
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;
`

const CarrierContainer = styled.div`
  position: absolute;
  padding: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
  bottom: ${rem(12)};
  inset-inline-start: ${rem(8)};
`

const CarrierImage = styled(Image)`
  max-width: ${rem(80)};
`

const LabelContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(4)};
  inset-inline-start: ${rem(4)};
`

interface Props {
  flightDeal: App.FlightDeal
  position?: number
  hasMultipleAirlines: boolean
}

function FlightDealImageTile(props: Props) {
  const { flightDeal, position, hasMultipleAirlines } = props
  const intl = useIntl()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const { fareTypes, fareCabins } = useFlightFareLabels()
  const dateFormatter = useIntlDateFormatter()
  const countdownOptions = useCountdownTagOptions({ tagPlaceId: 'deals-tile-item' })
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()
  const fareTypeLabel = fareTypes.find(type => type.value === flightDeal.fareType)?.label
  const fareClassLabel = fareCabins.find(type => type.value === flightDeal.fareClass)?.label

  const showFullDateLabel = useMemo(() => {
    const end = moment(flightDeal.salesEndDate)
    const leftToTheEnd = moment.duration(end.diff(moment()))
    const roundedEndDays = Math.floor(leftToTheEnd.asDays())
    return roundedEndDays > countdownOptions.showWithinXDays
  }, [flightDeal.salesEndDate, countdownOptions.showWithinXDays])
  const saleEndsIcon = useMemo(() => <LineAwardIcon />, [])
  const saleEndsLabel = useMemo(() => {
    if (!showFullDateLabel) {
      return null
    }

    return (
      <PromotionLabel icon={saleEndsIcon}>
        {intl.formatMessage(
          {
            id: 'IWBAyr1O3whtoFvwLZPA',
            defaultMessage: 'Sale ends {endDate}',
          },
          { endDate: dateFormatter(flightDeal.salesEndDate, INTL_DMY_CASUAL_SHORT_FORMAT) },
        )}
      </PromotionLabel>
    )
  }, [dateFormatter, flightDeal.salesEndDate, intl, saleEndsIcon, showFullDateLabel])

  const [inViewRef, hasBeenInView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  useEffect(() => {
    if (hasBeenInView && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: flightDeal,
        position,
        key: EventDataKey.FlightImpression,
      })
    }
  }, [hasBeenInView, flightDeal, position, dispatchOfferListEvent])

  const onClick = useCallback(() => {
    if (position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: flightDeal,
        position,
        key: EventDataKey.FlightClick,
      })
    }
  }, [position, flightDeal, dispatchOfferListEvent])

  const hasCarrierLogo = flightDeal.carrier.logo?.id || flightDeal.carrier.logo?.url

  return (
    <FlightCardContainer
      to={`/flight/deal/${flightDeal.id}`}
      target={isOpenNewTabOfferClickEnabled ? '_blank' : undefined}
      ref={inViewRef}
      onClick={onClick}
    >
      {flightDeal.heroImage?.id && (
        <ImageContainer paletteType="default">
          <Image
            aspectRatio="3:2"
            id={flightDeal.heroImage.id}
            fit="center"
            dpr={2}
            height="155"
            width="300"
          />
          <LabelContainer>
            <OfferUrgencyCountdownLabel
              endDate={flightDeal.salesEndDate}
              tagPlace="deals-tile-item"
              fallback={saleEndsLabel ?? undefined}
            />
          </LabelContainer>
          {hasMultipleAirlines && hasCarrierLogo && (
            <CarrierContainer>
              <CarrierImage
                height={34}
                fit="center"
                image={flightDeal.carrier.logo}
                alt={intl.formatMessage(
                  {
                    id: 'eOYj4lCm3wJdlngc2npv',
                    defaultMessage: 'Logo of {carrierName}',
                  },
                  { carrierName: flightDeal.carrier.name },
                )}
              />
            </CarrierContainer>
          )}
        </ImageContainer>
      )}
      <DealContainer direction="vertical" gap={8}>
        <BodyText variant="large" weight="bold">{flightDeal.destinationName}</BodyText>
        <FlightDealPoints flightDeal={flightDeal} />
        <div>
          <PriceRowPriceCaption>
            {intl.formatMessage(
              {
                id: 'Lo4fTqyPFfBt0j3irWG0',
                defaultMessage: '{fareClass} {fareType} from',
              },
              { fareClass: fareClassLabel, fareType: fareTypeLabel },
            )}
          </PriceRowPriceCaption>
          <PriceRowPrice
            size="L"
            price={flightDeal.perAdultPrice}
            saleUnit="adult"
          />
        </div>
      </DealContainer>
    </FlightCardContainer>
  )
}

export default FlightDealImageTile
